import { MenuContent, MenuRoot, MenuTrigger } from '@/components/ui/menu';
import { IconButton } from '@chakra-ui/react';
import { MoreHoriz } from 'iconoir-react';
import type { ComponentProps, ReactNode, Ref } from 'react';
import { rowActionIconButtonStyle } from './RowActionIconButton';

interface RowActionsMenuProps {
  /** Accessible name of the trigger ("More actions"). */
  label: string;
  /** The menu items. */
  children: ReactNode;
  minW?: string;
  triggerRef?: Ref<HTMLButtonElement>;
  portalled?: boolean;
  disabled?: boolean;
  menuProps?: Pick<
    ComponentProps<typeof MenuRoot>,
    'open' | 'onOpenChange' | 'highlightedValue' | 'onHighlightChange'
  >;
  contentProps?: Pick<
    ComponentProps<typeof MenuContent>,
    'w' | 'maxW' | 'overflow' | 'onKeyDownCapture'
  >;
}

/**
 * A table row's "more actions" menu behind the same 32px outlined square as
 * `RowActionIconButton`. Neither the trigger nor an item ever reaches the row,
 * which opens its own target: the content is portalled, but React still
 * bubbles its clicks through the row.
 */
export const RowActionsMenu = ({
  label,
  children,
  minW = '190px',
  triggerRef,
  portalled = true,
  disabled,
  menuProps,
  contentProps,
}: RowActionsMenuProps) => (
  <MenuRoot positioning={{ placement: 'bottom-end' }} {...menuProps}>
    <MenuTrigger asChild>
      <IconButton
        ref={triggerRef}
        disabled={disabled}
        aria-label={label}
        onClick={(event) => event.stopPropagation()}
        {...rowActionIconButtonStyle}
      >
        <MoreHoriz />
      </IconButton>
    </MenuTrigger>
    <MenuContent
      portalled={portalled}
      minW={minW}
      {...contentProps}
      onClick={(event) => event.stopPropagation()}
    >
      {children}
    </MenuContent>
  </MenuRoot>
);
